import React, { ReactElement } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';

import {
  gray40,
  gray80,
  primaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
} from '../../../constants/colors';
import { boldMd, regularLg, regularXl } from '../../../constants/location';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';

type Props = {
  open: boolean;
  onClose: () => void;
  onDelete: () => void;
  title: string;
  loading?: boolean;
  description?: string;
};

const styles = {
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
    marginTop: '32px',
    '@media screen and (max-width:960px)': {
      gap: '8px',
      marginTop: '20px',
    },
  },
  cancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    width: 'calc(50% - 8px)',
    '@media screen and (max-width:960px)': {
      width: 'calc(50% - 4px)',
    },
  },
  cancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  deleteButton: {
    height: '44px',
    width: 'calc(50% - 8px)',
    ...primaryButtonBackgroundColor,
  },
  deleteButtonText: {
    ...boldMd,
    color: 'white',
  },
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      boxSizing: 'border-box',
      margin: '16px',
      padding: '40px',
      '@media screen and (max-width: 960px)': {
        padding: '32px 16px',
      },
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
    '@media screen and (max-width:960px)': {
      marginBottom: '16px',
    },
  },
  icon: {
    height: '32px',
    width: '32px',
    '@media screen and (max-width:960px)': {
      height: '24px',
      width: '24px',
    },
  },
  iconButton: {
    padding: 0,
  },
  title: {
    ...regularXl,
    // 長い名称（グループ名等・連続文字列）でも横あふれさせず折り返す。
    // flex 子要素が縮めるよう minWidth:0 も付与する。
    minWidth: 0,
    overflowWrap: 'anywhere',
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  // 本文（description）も長い連続文字列（OCR読取値等）で横あふれさせず折り返す
  description: {
    overflowWrap: 'anywhere',
  },
};

const DeleteModal = ({
  open,
  onClose,
  onDelete,
  title,
  loading = false,
  description,
}: Props): ReactElement => {
  const proxyDialogSx = useProxyDialogSx();

  return (
    <Dialog open={open} sx={[styles.dialog, proxyDialogSx]}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>{title}を削除</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton}>
          <Close sx={styles.icon} />
        </IconButton>
      </Box>
      <Typography sx={styles.description}>
        {description ?? '削除すると元に戻すことはできません。よろしいですか？'}
      </Typography>
      <Box sx={styles.buttonWrapper}>
        <Button sx={styles.cancelButton} onClick={onClose} disabled={loading}>
          <Typography sx={styles.cancelButtonText}>キャンセル</Typography>
        </Button>
        <Button sx={styles.deleteButton} onClick={onDelete} disabled={loading}>
          <Typography sx={styles.deleteButtonText}>{loading ? '削除中...' : '削除する'}</Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default DeleteModal;
